import { bcs } from '@mysten/sui/bcs';
import { Transaction } from '@mysten/sui/transactions';
import { normalizeStructTag, normalizeSuiAddress } from '@mysten/sui/utils';

import {
  BcsType,
  MoveCallTransactionArgumentType,
  MoveCallTransactionType,
  SplitCoinTransactionType,
  TransactionCommand,
} from '../types/sui';

/** Legacy Transaction.blockData input / argument shape still used by scallop decoders. */
type LegacyTxInput = {
  type?: string;
  value?: any;
  kind?: string;
  index?: number;
  Pure?: number[];
};

class MoveCallHelper {
  private cmdIdx: number;

  constructor(
    public readonly moveCall: TransactionCommand | undefined,
    public readonly transaction: Transaction,
    index = 0,
  ) {
    const indexes: number[] = [];
    if (moveCall?.MoveCall) {
      transaction.getData().commands.filter((t, idx) => {
        if (t.$kind === 'MoveCall') {
          const target = `${t.MoveCall.package}::${t.MoveCall.module}::${t.MoveCall.function}`;
          const moveCallTarget = `${moveCall.MoveCall.package}::${moveCall.MoveCall.module}::${moveCall.MoveCall.function}`;
          const match = target === moveCallTarget;
          if (match) {
            indexes.push(idx);
            return true;
          }
          return false;
        }
        return false;
      });
    }
    this.cmdIdx = indexes[index];
  }

  get txBlockTransactions(): MoveCallTransactionType {
    return this.transaction.blockData.transactions[this.cmdIdx] as MoveCallTransactionType;
  }

  decodeSharedObjectId(argIndex: number) {
    const input = this.getInputParam(argIndex);
    return MoveCallHelper.getSharedObjectId(input);
  }

  decodeOwnedObjectId(argIndex: number) {
    const input = this.getInputParam(argIndex);
    return MoveCallHelper.getOwnedObjectId(input);
  }

  decodeInputU64(argIndex: number) {
    const strVal = this.decodePureArg<string>(argIndex, 'U64');
    return Number(strVal);
  }

  decodeInputU8(argIndex: number) {
    const strVal = this.decodePureArg<string>(argIndex, 'U8');
    return Number(strVal);
  }

  decodeInputAddress(argIndex: number) {
    const input = this.decodePureArg<string>(argIndex, 'Address');
    return normalizeSuiAddress(input);
  }

  decodeInputString(argIndex: number) {
    return this.decodePureArg<string>(argIndex, 'String');
  }

  decodeInputBool(argIndex: number) {
    return this.decodePureArg<boolean>(argIndex, 'Bool');
  }

  decodePureArg<T>(argIndex: number, type: BcsType) {
    const input = this.getInputParam(argIndex);
    return MoveCallHelper.getPureInputValue<T>(input, type);
  }

  getInputParam(argIndex: number) {
    const arg = this.txBlockTransactions.arguments[argIndex] as MoveCallTransactionArgumentType;
    if (arg.kind !== 'Input') {
      throw new Error('not input type');
    }
    return arg;
  }

  getNestedInputParam<T = SplitCoinTransactionType>(argIndex: number) {
    const arg = this.txBlockTransactions.arguments[argIndex] as MoveCallTransactionArgumentType;
    if (arg.kind !== 'NestedResult' && arg.kind !== 'Result') {
      throw new Error('not nested or result type');
    }
    return this.transaction.blockData.transactions[arg.index] as T;
  }

  static getPureInputValue<T>(input: LegacyTxInput, type: BcsType) {
    if (input.type !== 'pure') {
      throw new Error('not pure argument');
    }
    if (typeof input.value === 'object' && 'Pure' in input.value) {
      const bcsNums = input.value.Pure;
      return bcs[type].parse(new Uint8Array(bcsNums)) as T;
    }
    return input.value as T;
  }

  static getOwnedObjectId(input: LegacyTxInput) {
    if (input.type !== 'object') {
      throw new Error(`not object argument: ${JSON.stringify(input)}`);
    }
    if (typeof input.value === 'object') {
      if (!('Object' in input.value) || !('ImmOrOwned' in input.value.Object)) {
        throw new Error('not ImmOrOwned');
      }
      return normalizeSuiAddress(input.value.Object.ImmOrOwned.objectId as string);
    }
    return normalizeSuiAddress(input.value as string);
  }

  static getSharedObjectId(input: LegacyTxInput) {
    if (input.type !== 'object') {
      throw new Error(`not object argument: ${JSON.stringify(input)}`);
    }
    if (typeof input.value !== 'object') {
      return normalizeSuiAddress(input.value as string);
    }
    if (!('Object' in input.value) || !('Shared' in input.value.Object)) {
      throw new Error('not Shared');
    }
    return normalizeSuiAddress(input.value.Object.Shared.objectId as string);
  }

  static getPureInput<T>(input: LegacyTxInput) {
    if (input.type !== 'pure') {
      throw new Error('not pure argument');
    }
    if (typeof input.value !== 'object') {
      return input.value as T;
    }
    if (!('Pure' in input.value)) {
      throw new Error('Pure not in value');
    }
    const bcsVal = input.value.Pure;
    return bcs.U64.parse(new Uint8Array(bcsVal)) as T;
  }

  typeArg(index: number) {
    return normalizeStructTag(this.txBlockTransactions.typeArguments[index]);
  }

  txArg(index: number) {
    return this.transaction.blockData.inputs[index];
  }
}

export default MoveCallHelper;
